import { useRef } from 'react';
import { animate, useMotionValue, useTransform, type MotionValue } from 'motion/react';

const interactive = (el: EventTarget | null) => el instanceof Element && !!el.closest('button, a, input, textarea, select, [role="slider"], [data-no-gesture]');

export interface WindowGesture {
  x: MotionValue<number>;
  y: MotionValue<number>;
  rotateY: MotionValue<number>;
  scale: MotionValue<number>;
  opacity: MotionValue<number>;
  handlers: {
    onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
    onPointerMove: (e: React.PointerEvent<HTMLElement>) => void;
    onPointerUp: (e: React.PointerEvent<HTMLElement>) => void;
    onPointerCancel: (e: React.PointerEvent<HTMLElement>) => void;
  };
}

/**
 * Direct manipulation of a window by its header (finger, pen or mouse):
 * fling sideways to move to the next/previous window, pull down to put it away.
 * Movement follows the pointer; release decides with distance and velocity.
 */
export function useWindowGesture(opts: { width: () => number; canSwipe: (dir: 1 | -1) => boolean; onSwipe: (dir: 1 | -1) => void; onDismiss: () => void; reducedMotion: boolean }): WindowGesture {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateY = useTransform(x, [-600, 0, 600], [16, 0, -16]);
  const scaleX = useTransform(x, [-600, 0, 600], [0.92, 1, 0.92]);
  const scaleY = useTransform(y, [0, 500], [1, 0.78]);
  const scale = useTransform([scaleX, scaleY], ([a, b]) => Math.min(a as number, b as number));
  const opacityX = useTransform(x, [-700, 0, 700], [0.35, 1, 0.35]);
  const opacityY = useTransform(y, [0, 500], [1, 0.45]);
  const opacity = useTransform([opacityX, opacityY], ([a, b]) => Math.min(a as number, b as number));
  const s = useRef<{
    id: number;
    x0: number;
    y0: number;
    axis: 'x' | 'y' | null;
    samples: Array<{ t: number; x: number; y: number }>;
  } | null>(null);

  const spring = { type: 'spring' as const, stiffness: 420, damping: 36 };
  const settle = () => {
    void animate(x, 0, opts.reducedMotion ? { duration: 0 } : spring);
    void animate(y, 0, opts.reducedMotion ? { duration: 0 } : spring);
  };

  return {
    x,
    y,
    rotateY,
    scale,
    opacity,
    handlers: {
      onPointerDown: (e) => {
        if (e.button > 0 || interactive(e.target)) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        s.current = {
          id: e.pointerId,
          x0: e.clientX,
          y0: e.clientY,
          axis: null,
          samples: [{ t: performance.now(), x: e.clientX, y: e.clientY }],
        };
      },
      onPointerMove: (e) => {
        const g = s.current;
        if (!g || g.id !== e.pointerId) return;
        const dx = e.clientX - g.x0;
        const dy = e.clientY - g.y0;
        if (!g.axis) {
          if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) g.axis = 'x';
          else if (dy > 10 && dy > Math.abs(dx)) g.axis = 'y';
          else return;
        }
        g.samples.push({ t: performance.now(), x: e.clientX, y: e.clientY });
        if (g.samples.length > 6) g.samples.shift();
        if (g.axis === 'x') {
          const dir = dx < 0 ? 1 : -1;
          // Resistance when there is nowhere to go.
          x.set(opts.canSwipe(dir as 1 | -1) ? dx : dx * 0.25);
        } else y.set(Math.max(0, dy));
      },
      onPointerUp: (e) => {
        const g = s.current;
        s.current = null;
        if (!g || !g.axis) return;
        const first = g.samples[0]!;
        const last = g.samples[g.samples.length - 1]!;
        const dt = Math.max(1, last.t - first.t);
        const vx = (last.x - first.x) / dt;
        const vy = (last.y - first.y) / dt;
        const w = opts.width();
        if (g.axis === 'x') {
          const dx = e.clientX - g.x0;
          const dir: 1 | -1 = dx < 0 ? 1 : -1;
          if ((Math.abs(dx) > w * 0.22 || Math.abs(vx) > 0.55) && opts.canSwipe(dir)) {
            void animate(x, -dir * w * 1.05, opts.reducedMotion ? { duration: 0 } : { duration: 0.22, ease: [0.4, 0, 1, 1] }).then(() => opts.onSwipe(dir));
            return;
          }
        } else {
          const dy = e.clientY - g.y0;
          if (dy > 140 || vy > 0.7) {
            void animate(y, 600, opts.reducedMotion ? { duration: 0 } : { duration: 0.24, ease: [0.4, 0, 1, 1] }).then(() => opts.onDismiss());
            return;
          }
        }
        settle();
      },
      onPointerCancel: () => {
        s.current = null;
        settle();
      },
    },
  };
}

/** Two-finger pinch (touch) or trackpad pinch (ctrl + wheel). In: overview; out: optional. */
export function usePinch(onPinchIn: () => void, onPinchOut?: () => void) {
  const touches = useRef(new Map<number, { x: number; y: number }>());
  const startDist = useRef<number | null>(null);
  const wheel = useRef(0);
  const dist = () => {
    const [a, b] = [...touches.current.values()];
    return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
  };
  return {
    onPointerDown: (e: React.PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      touches.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (touches.current.size === 2) startDist.current = dist();
    },
    onPointerMove: (e: React.PointerEvent) => {
      if (!touches.current.has(e.pointerId)) return;
      touches.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (startDist.current && touches.current.size === 2) {
        const d = dist();
        if (d < startDist.current * 0.62) {
          startDist.current = null;
          onPinchIn();
        } else if (onPinchOut && d > startDist.current * 1.5) {
          startDist.current = null;
          onPinchOut();
        }
      }
    },
    onPointerUp: (e: React.PointerEvent) => {
      touches.current.delete(e.pointerId);
      if (touches.current.size < 2) startDist.current = null;
    },
    onPointerCancel: (e: React.PointerEvent) => {
      touches.current.delete(e.pointerId);
      if (touches.current.size < 2) startDist.current = null;
    },
    onWheel: (e: React.WheelEvent) => {
      if (!e.ctrlKey) return;
      wheel.current += e.deltaY;
      if (wheel.current > 60) {
        wheel.current = 0;
        onPinchIn();
      } else if (onPinchOut && wheel.current < -60) {
        wheel.current = 0;
        onPinchOut();
      }
    },
  };
}

/**
 * Two fingers anywhere on a window: swipe sideways to move to the next or previous window,
 * swipe down to put it away. One-finger scrolling inside the window is untouched.
 */
export function useTwoFingerSwipe(opts: { onSwipe: (dir: 1 | -1) => void; onDown: () => void }) {
  const touches = useRef(new Map<number, { x: number; y: number }>());
  const start = useRef<{ x: number; y: number } | null>(null);
  const done = useRef(false);
  const centroid = () => {
    const pts = [...touches.current.values()];
    return {
      x: pts.reduce((s, p) => s + p.x, 0) / pts.length,
      y: pts.reduce((s, p) => s + p.y, 0) / pts.length,
    };
  };
  const end = (e: React.PointerEvent) => {
    touches.current.delete(e.pointerId);
    if (touches.current.size < 2) {
      start.current = null;
      done.current = false;
    }
  };
  return {
    onPointerDown: (e: React.PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      touches.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (touches.current.size === 2) {
        start.current = centroid();
        done.current = false;
      }
    },
    onPointerMove: (e: React.PointerEvent) => {
      if (!touches.current.has(e.pointerId)) return;
      touches.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (!start.current || done.current || touches.current.size !== 2) return;
      const c = centroid();
      const dx = c.x - start.current.x;
      const dy = c.y - start.current.y;
      if (Math.abs(dx) > 90 && Math.abs(dx) > Math.abs(dy) * 1.4) {
        done.current = true;
        opts.onSwipe(dx < 0 ? 1 : -1);
      } else if (dy > 110 && dy > Math.abs(dx) * 1.4) {
        done.current = true;
        opts.onDown();
      }
    },
    onPointerUp: end,
    onPointerCancel: end,
  };
}
